import { enUS, ja } from "date-fns/locale";
import { TemplateInspectionItemMedia } from "../Inspection/CreateInspections";
import { HourUnitLang, HourUnitMap, INVALID_URL } from "@app/constants";

export const NUMERIC_SMR_MAX = 999999.9;
export const NUMERIC_ODO_MAX = 999999.9;
export const NUMERIC_INPUT_PATTERN = /^\d*\.?\d{0,1}$/;
export const MAINTENANCE_DARK_BLUE = "#140a9a";

export const MAINTENANCE_ADD_BUTTON_SX = {
  minWidth: 120,
  "& .MuiButton-startIcon svg path": {
    fill: MAINTENANCE_DARK_BLUE,
  },
  "&:hover .MuiButton-startIcon svg path": {
    fill: "#ffffff",
  },
} as const;

export const dateLocales: Record<string, typeof enUS> = {
  en: enUS,
  ja,
};

export function stripLeadingZerosFromNumeric(value: string): string {
  if (!value) return value;
  if (value.startsWith("0.")) return value;

  if (value.includes(".")) {
    const [intPart, ...decParts] = value.split(".");
    const decPart = decParts.join("").replace(/\./g, "");
    const strippedInt = intPart.replace(/^0+/, "") || "0";
    if (value.endsWith(".") && decPart === "") {
      return `${strippedInt}.`;
    }
    return `${strippedInt}.${decPart}`;
  }

  return value.replace(/^0+/, "") || "0";
}

export function enforceNumericDecimalFormat(value: string): string {
  const dotIndex = value.indexOf(".");
  if (dotIndex === -1) return value;

  const intPart = value.slice(0, dotIndex);
  const decPart = value.slice(dotIndex + 1).replace(/\./g, "");
  if (value.endsWith(".") && decPart === "") {
    return `${intPart}.`;
  }
  return `${intPart}.${decPart.slice(0, 1)}`;
}

export function sanitizeNumericInputValue(
  raw: string,
  fallback: string,
): string {
  if (!NUMERIC_INPUT_PATTERN.test(raw)) {
    return fallback;
  }
  const withoutLeadingZeros = stripLeadingZerosFromNumeric(raw);
  return enforceNumericDecimalFormat(withoutLeadingZeros);
}

export function isNumericValueValid(value: string, max: number): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.endsWith(".")) {
    return false;
  }
  if (!/^\d+\.\d$/.test(trimmed) && !/^\d+$/.test(trimmed)) {
    return false;
  }
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= max;
}

export function normalizeNumericOnBlur(value: string, max: number): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  let normalized = enforceNumericDecimalFormat(
    stripLeadingZerosFromNumeric(trimmed),
  );
  if (/^\d+$/.test(normalized)) {
    normalized = `${normalized}.0`;
  } else if (normalized.endsWith(".")) {
    normalized = `${normalized}0`;
  }

  const parsed = parseFloat(normalized);
  if (Number.isFinite(parsed) && parsed > max) {
    return String(max);
  }

  return normalized;
}

export function stripLabelRequiredMarker(label: string): string {
  return label.replace(/\s*\*+\s*$/, "").trim();
}

export function getOdometerLabel(t: (key: string) => string): string {
  return stripLabelRequiredMarker(t("odometer"));
}

export function cloneBlobForPlayback(blob: Blob): Blob {
  const type =
    blob.type && blob.type !== "application/octet-stream"
      ? blob.type
      : "audio/wav";
  if (blob.type === type) return blob;
  return new Blob([blob], { type });
}

export function isLikelyVideoPath(url: string): boolean {
  const path = url.split("?")[0].toLowerCase();
  return /\.(mp4|mov|webm|m4v|avi)$/i.test(path);
}

export function getTemplateMediaPreviewUrl(
  media: TemplateInspectionItemMedia,
): string | undefined {
  const url = media.mediaUrl?.trim();
  if (!url || url === INVALID_URL) {
    return undefined;
  }
  return url;
}

export function openImageInViewer(
  url: string,
  setImage: (s: string) => void,
  setOpen: (v: boolean) => void,
): void {
  if (!url) return;
  if (isLikelyVideoPath(url)) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  if (url.startsWith("blob:")) {
    const img = new Image();
    img.onload = () => {
      setImage(url);
      setOpen(true);
    };
    img.onerror = () => {
      window.open(url, "_blank", "noopener,noreferrer");
    };
    img.src = url;
    return;
  }
  setImage(url);
  setOpen(true);
}

export function isTemplateMediaVideo(
  media: TemplateInspectionItemMedia,
): boolean {
  const mime = (media.mimeType || "").toLowerCase();
  if (mime.includes("video")) {
    return true;
  }
  return (
    isLikelyVideoPath(media.mediaUrl || "") ||
    isLikelyVideoPath(media.fileName || "")
  );
}

export function formatIntervalLabel(
  code: string,
  fallbackName: string,
  localeCode: string,
): string {
  const match = code.match(/^(\d+)H$/i);
  if (!match) {
    // OTHER, or any non-numeric code — fall back to whatever name the API/i18n gave
    return fallbackName;
  }

  const hours = match[1];
  const unit = HourUnitMap[localeCode as HourUnitLang] ?? HourUnitMap.en;

  return localeCode === "ja" ? `${hours}${unit}` : `${hours} ${unit}`;
}

/**
 * Same as `formatIntervalLabel`, but for the Create Maintenance item-group
 * header specifically: abbreviates the "hours"-word locales (en/es/fr/pt,
 * which all currently share the literal "hours" unit) to "h", and appends
 * the localized word for "Maintenance" — e.g. "500h Maintenance" in en.
 * Locales with their own script for the hour unit (ja/ar/ur/th/vi/lo/km/ru)
 * keep their existing unit word, just with "Maintenance" appended.
 */
export function formatIntervalMaintenanceLabel(
  code: string,
  fallbackName: string,
  localeCode: string,
  maintenanceWord: string,
): string {
  const match = code.match(/^(\d+)H$/i);
  if (!match) {
    // OTHER, or any non-numeric code — fall back to whatever name the API/i18n gave
    return fallbackName;
  }

  const hours = match[1];
  const unit = HourUnitMap[localeCode as HourUnitLang] ?? HourUnitMap.en;
  const isJapanese = localeCode === "ja";
  const usesGenericHoursWord = unit === "hours";

  const hourPart = usesGenericHoursWord
    ? `${hours}h`
    : isJapanese
      ? `${hours}${unit}`
      : `${hours} ${unit}`;

  return isJapanese
    ? `${hourPart}${maintenanceWord}`
    : `${hourPart} ${maintenanceWord}`;
}
